// Faculty photo overrides sourced from webadmin's published content, not
// from staff.ts. See infra/webadmin-image-ingest-spec.md for the pipeline
// that gets a photo here, and the note in that doc's "Open question" section
// (resolved 2026-09-17: webadmin publishes a raw external URL, so
// scripts/process-content-images.mjs reprocesses it through our own image
// pipeline at build time and rewrites it to a bare key before this file ever
// sees it).
//
// Keyed by staff.ts slug (via normalizePublishedSlug — most published slugs
// already equal their staff.ts counterpart, a handful don't; see
// publishedFacultySlugAliases.ts for exactly which and how each was
// verified), so every caller can look a photo up by the identity it already
// has rather than knowing anything about slug mismatches itself. A slug
// with no entry here should fall back to staff.ts's own photo (or the
// initials placeholder) — this map is additive, never a replacement data
// source, and this file only ever handles photos; see mergedFaculty.ts for
// where name/designation/department/category are merged in too.
import type { Media } from "@zivin-labs/site-content";
import { useCollection } from "./siteContent";
import { normalizePublishedSlug } from "./publishedFacultySlugAliases";

/**
 * The shape this file actually reads out of the published "faculty"
 * collection — narrower than whatever type the package's own generic
 * carries, so this file has one stable local contract rather than depending
 * on every field webadmin happens to publish (qualifications, publications,
 * research areas, ... none of which are used here).
 */
interface PublishedFacultyRecord {
  slug: string;
  name: string;
  photo: { url: string; alt?: string; width?: number; height?: number } | null;
}

/**
 * Published faculty photos, keyed by staff.ts slug. A slug with no entry
 * here should fall back to staff.ts's own photo (or the initials
 * placeholder) exactly as it does today — this map is additive, never a
 * replacement data source.
 */
export function usePublishedFacultyPhotos(): Map<string, Media> {
  const { items } = useCollection("faculty");
  const records = items as unknown as PublishedFacultyRecord[];
  const map = new Map<string, Media>();
  for (const r of records) {
    const p = r.photo;
    if (!p?.url || !p.width || !p.height) continue;
    map.set(normalizePublishedSlug(r.slug), {
      url: p.url,
      alt: p.alt || r.name,
      width: p.width,
      height: p.height,
    });
  }
  return map;
}
